import { Theme } from '@mui/material';

export const SideBarCSS = {
  // Main Layout
  container: {
    width: '100%',
    height: '100%',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const
  },
  content: (theme: Theme) => ({
    padding: theme.spacing?.(3) || '24px',
    flexGrow: 1
  }),

  // Typography
  sidebarTitle: {
    color: 'var(--color-primary)',
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: { xs: '24px', md: '30px' },
    lineHeight: 'var(--line-height-24)'
  },
  sectionTitle: {
    color: 'var(--color-text-mid)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-20)',
    lineHeight: 'var(--line-height-24)',
    marginBottom: '6px'
  },
  checkboxOptionLabel: {
    color: 'var(--color-text-mid)',
    fontSize: 'var(--font-size-16)',
    fontWeight: 500,
    lineHeight: 'var(--line-height-22)',
    fontFamily: 'Inter'
  },
  emailFieldLabel: {
    color: 'var(--color-text-purple)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    marginBottom: '6px'
  },
  textAreaFooterText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-16)',
    lineHeight: 'var(--line-height-24)',
    textAlign: 'right' as const,
    color: 'var(--color-text-purple)'
  },
  aiSummarySectionTitle: {
    color: 'var(--color-text-mid)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-20)',
    lineHeight: 'var(--line-height-24)'
  },

  // Recipient Styles
  recipientContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
    border: '1px solid var(--color-border-light2)',
    padding: 0,
    borderRadius: '8px',
    position: 'relative' as const,
    marginTop: '5px'
  },
  recipientList: {
    width: '100%',
    padding: 1,
    overflow: 'auto' as const,
    maxHeight: '185px',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 1,
    alignItems: 'flex-start'
  },
  chip: {
    backgroundColor: 'var(--color-success-bg)'
  },
  closeIcon: {
    color: 'var(--color-text-dark) !important'
  },
  autocomplete: {
    padding: '0px',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      padding: '0px'
    }
  },
  emailInput: {
    width: '100%',
    border: 'none',
    flexGrow: 1,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
  },
  recipientFooter: {
    width: '100%',
    padding: '5px',
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center'
  },

  // Custom Icon Styles
  customIconContainer: {
    width: '100%',
    height: '180px',
    border: '1px solid var(--color-bg-slate)',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative' as const,
    backgroundColor: 'var(--color-near-white)'
  },
  iconImage: {
    maxWidth: '50%',
    maxHeight: '50%',
    objectFit: 'contain' as const
  },
  iconActions: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    display: 'flex',
    gap: 1
  },
  editIconButton: {
    border: '1px solid var(--color-bg-slate)',
    backgroundColor: 'white',
    color: 'var(--color-text-tertiary)',
    '&:hover': { backgroundColor: 'var(--color-bg-subtle)' }
  },
  deleteIconButton: {
    border: '1px solid #FECACA',
    backgroundColor: 'white',
    color: '#EF4444',
    '&:hover': { backgroundColor: '#FEF2F2' }
  },
  addIconBox: {
    width: '120px',
    height: '110px',
    border: '1px dashed var(--color-border)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent'
  },
  addIconText: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    fontWeight: 500,
    color: 'var(--color-text-tertiary)'
  },
  addIconBoxDynamic: (isUpdating: boolean) => ({
    width: '120px',
    height: '110px',
    border: '1px dashed #D0D5DD',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    cursor: isUpdating ? 'default' : 'pointer',
    '&:hover': {
      backgroundColor: isUpdating ? 'transparent' : '#F9FAFB'
    }
  }),

  // AI Summary Config Styles
  aiConfigContainer: (theme: Theme) => ({
    border: '1px solid var(--color-bg-slate)',
    borderRadius: (theme as any).general?.borderRadiusSm || '6px',
    padding: '12px',
    backgroundColor: 'var(--color-near-white)',
    marginBottom: '16px',
    width: '100%'
  }),
  aiConfigControlRow: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center',
    cursor: 'pointer'
  },
  aiCheckbox: {
    color: 'var(--color-border)',
    '&.Mui-checked': {
      color: 'var(--color-primary)'
    }
  },
  aiToggleGroup: (theme: Theme) => ({
    width: '100%',
    height: '40px',
    borderRadius: '6px',
    overflow: 'hidden',
    '& .MuiToggleButton-root': {
      flex: 1,
      textTransform: 'none' as const,
      fontSize: 'var(--font-size-14)',
      fontWeight: 500,
      color: 'var(--color-text-dark)',
      backgroundColor: 'white',
      borderColor: 'var(--color-border)',
      borderRightWidth: '0px',
      '&:last-child': {
        borderRightWidth: '1px'
      },
      '&.Mui-selected': {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-white)',
        borderColor: 'var(--color-primary)',
        borderRightWidth: '1px',
        zIndex: 1,
        '&:hover': {
          backgroundColor: 'var(--color-purple-deep)'
        }
      }
    }
  }),
  aiToggleFirst: (theme: Theme) => ({
    borderTopLeftRadius: '6px !important',
    borderBottomLeftRadius: '6px !important',
    borderTopRightRadius: '0px !important',
    borderBottomRightRadius: '0px !important'
  }),
  aiToggleLast: (theme: Theme) => ({
    borderTopRightRadius: '6px !important',
    borderBottomRightRadius: '6px !important',
    borderTopLeftRadius: '0px !important',
    borderBottomLeftRadius: '0px !important'
  }),
  aiToggleMiddle: {
    borderRadius: '0px !important'
  },
  aiSettingsContainer: {
    mt: 1
  },
  aiSettingsFieldLabel: {
    color: '#686685',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    marginBottom: '6px',
    mt: 2
  },

  // Footer Styles
  footerContainer: (theme: Theme) => ({
    position: 'sticky' as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing?.(2) || '16px',
    backgroundColor: theme.palette?.background?.paper || 'var(--color-white)',
    borderTop: `1px solid ${
      theme.palette?.divider || 'var(--color-border-grey)'
    }`,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'space-between' as const,
    gap: theme.spacing?.(1) || '8px',
    boxShadow: '0px -4px 10px rgba(0,0,0,0.05)',
    flexDirection: 'column-reverse' as const
  }),
  footerBtn: {
    maxHeight: '40px'
  },
  footerButtonReset: (theme: Theme) => ({
    borderColor:
      (theme as any).colors?.gray?.[300] ||
      theme.palette?.grey?.[300] ||
      'var(--color-border-light2)',
    color:
      (theme as any).colors?.gray?.[700] ||
      theme.palette?.text?.primary ||
      'var(--color-text-dark3)',
    fontWeight: (theme as any).weights?.semibold || 600,
    '&:hover': {
      borderColor:
        (theme as any).colors?.gray?.[300] ||
        theme.palette?.grey?.[300] ||
        'var(--color-border-light2)',
      backgroundColor:
        (theme as any).colors?.gray?.[50] ||
        theme.palette?.grey?.[50] ||
        'var(--color-bg-off-white)'
    }
  }),
  footerButtonSave: {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none'
    }
  },

  // AI Summary Box (Green Background) Styles
  aiSummaryBox: {
    background: 'linear-gradient(135deg, #D4F4DD 0%, #E8F9ED 100%)',
    border: '1px solid var(--color-success-pastel)',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '16px'
  },
  aiSummaryHeader: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: 1,
    marginBottom: '16px'
  },
  aiSummaryIconWrapper: {
    backgroundColor: 'var(--color-white)',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
  },
  aiSummaryMuiIcon: {
    color: 'var(--color-success-dark)',
    fontSize: 'var(--font-size-18)'
  },
  aiSummaryTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#101828',
    fontFamily: 'Inter'
  },
  aiSummaryText: {
    fontSize: 'var(--font-size-14)',
    lineHeight: 1.6,
    color: 'var(--color-text-darkest)',
    fontFamily: 'Inter',
    whiteSpace: 'pre-wrap' as const
  },
  aiSummaryTextBody: {
    fontSize: '14px',
    lineHeight: '23px',
    fontWeight: 400,
    color: '#101828',
    fontFamily: 'Inter',
    whiteSpace: 'pre-line' as const
  },
  aiSummaryTextHeader: {
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '28px',
    mb: 1.5,
    fontFamily: 'Inter',
    color: '#101828',
    textDecoration: 'underline',
    textUnderlineOffset: '6px'
  },
  aiSummaryFooter: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid var(--color-success-pastel)'
  },
  aiSummaryGeneratedBy: {
    fontSize: '12px',
    color: 'var(--color-text-tertiary)',
    fontStyle: 'italic' as const,
    fontFamily: 'Inter'
  },
  aiSummaryContentWrapper: {
    paddingTop: '8px',
    paddingBottom: '8px'
  },

  // AI Summary Placeholder Styles
  aiSummaryPlaceholder: {
    backgroundColor: '#F9FAFB',
    border: '1px dashed #D0D5DD',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    gap: 2
  },
  aiSummaryPlaceholderHeader: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: 1,
    mb: 1
  },
  aiSummaryPlaceholderIcon: {
    color: '#6941C6',
    fontSize: '24px'
  },
  aiSummaryPlaceholderTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#101828'
  },
  aiSummaryPlaceholderText: {
    fontSize: '14px',
    color: '#475467',
    lineHeight: 1.6,
    maxWidth: '500px'
  },
  aiSummaryPlaceholderButton: {
    textTransform: 'none' as const,
    fontWeight: 600,
    backgroundColor: '#120242',
    maxHeight: '40px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#0D0131'
    }
  },

  // Viewport Styles
  viewportContainer: (theme: Theme) => ({
    flexGrow: 1,
    height: 'calc(100vh - 64px)',
    overflow: 'hidden' as const,
    backgroundColor: theme.palette?.grey?.[200] || 'var(--color-bg-mid-grey)',
    display: 'flex',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    position: 'relative' as const
  }),
  pdfFrame: (theme: Theme) => ({
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: theme.palette?.grey?.[200] || 'var(--color-bg-mid-grey)'
  }),
  errorPaper: {
    p: 4,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    maxWidth: 400
  },
  errorIcon: {
    fontSize: 48,
    mb: 2
  },

  // Preview Canvas Styles
  canvasWrapper: {
    width: 'fit-content'
  },
  canvasPage: (isExporting?: boolean) => ({
    width: isExporting ? '1130px' : '1128px',
    minWidth: isExporting ? '1130px' : '1128px',
    background: 'var(--color-bg-cream)',
    padding: isExporting ? '2rem' : '2rem',
    fontFamily: 'Inter, sans-serif'
  }),
  logoContainer: {
    width: '20%',
    height: '20%',
    margin: '0 auto 10px',
    overflow: 'hidden' as const
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const
  },
  canvasTitle: {
    textAlign: 'left' as const,
    fontSize: 'var(--font-size-30)',
    marginBottom: '1rem',
    fontFamily: 'Inter',
    fontWeight: 680,
    color: 'var(--color-primary)'
  },
  metaDataBar: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    fontSize: 'var(--font-size-20)',
    marginBottom: '1rem',
    fontFamily: 'Inter',
    color: 'var(--color-primary)',
    position: 'relative' as const
  },
  metaDataUrl: {
    maxWidth: '30%',
    wordBreak: 'break-all' as const
  },
  metaDataCompare: {
    position: 'absolute' as const,
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap' as const
  },

  // Placeholder Styles
  placeholderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: '400px',
    padding: 4,
    textAlign: 'center' as const,
    backgroundColor: 'var(--color-bg-cream)',
    border: '1px dashed var(--color-border)',
    borderRadius: '12px',
    margin: '2rem'
  },
  placeholderIcon: {
    fontSize: '64px',
    color: 'var(--color-text-tertiary)',
    marginBottom: 2
  },
  placeholderTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    color: 'var(--color-primary)',
    marginBottom: 1
  },
  placeholderText: {
    fontFamily: 'Inter',
    color: 'var(--color-text-tertiary)',
    maxWidth: '400px'
  },

  // Loading/Error Styles
  overlayBox: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--color-white)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    zIndex: 100
  },
  fullPageLoading: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--color-bg-subtle)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    zIndex: 1000
  },
  loadingText: {
    mt: 2,
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-18)',
    color: 'var(--color-primary)'
  },
  errorContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: '32px',
    textAlign: 'center' as const,
    backgroundColor: 'var(--color-bg-subtle)',
    zIndex: 1001
  },
  errorTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-24)',
    lineHeight: 'var(--line-height-32)',
    color: 'var(--color-primary)',
    mb: 1
  },
  errorMessage: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-16)',
    lineHeight: 'var(--line-height-24)',
    color: 'var(--color-text-mid)',
    mb: 4,
    maxWidth: '480px'
  },
  errorActions: {
    display: 'flex',
    gap: 2,
    justifyContent: 'center',
    alignItems: 'center'
  },
  errorRetryBtn: {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: 3,
    fontSize: 'var(--font-size-14)',
    fontWeight: 600,
    maxHeight: '42px',
    height: '42px',
    '&:hover': {
      backgroundColor: 'var(--color-purple-deep)'
    }
  },
  errorHomeBtn: {
    backgroundColor: 'white',
    color: 'var(--color-text-dark)',
    textTransform: 'none' as const,
    borderRadius: '8px',
    border: '1px solid var(--color-border)',
    px: 3,
    fontSize: 'var(--font-size-14)',
    fontWeight: 600,
    maxHeight: '42px',
    height: '42px',
    '&:hover': {
      backgroundColor: 'var(--color-bg-subtle)'
    }
  },

  // Header Styles
  previewHeaderContainer: (theme: Theme) => ({
    width: '100%',
    height: '64px',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    padding: theme.spacing?.(2, 3) || '16px 24px',
    backgroundColor: theme.palette?.common?.white || 'var(--color-white)',
    borderBottom: `1px solid ${
      theme.palette?.divider || 'var(--color-bg-mid-grey)'
    }`,
    flexShrink: 0
  }),
  pageCounter: (theme: Theme) => ({
    fontWeight: 500,
    color: theme.palette?.text?.primary || 'var(--color-text-dark3)'
  }),

  // Interactive Section Styles
  sectionHoverWrapper: {
    position: 'relative' as const,
    '&:hover .section-actions': {
      opacity: 1
    },
    '&:hover': {
      outline: '2px dashed #B2DDFF',
      borderRadius: '4px'
    }
  },
  sectionActions: {
    position: 'absolute' as const,
    top: -12,
    right: 8,
    display: 'flex',
    gap: 1,
    opacity: 0,
    transition: 'opacity 0.2s',
    zIndex: 10,
    backgroundColor: 'white',
    padding: '4px',
    borderRadius: '8px',
    boxShadow:
      '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
  },
  actionButton: {
    padding: '4px',
    color: 'var(--color-text-tertiary)',
    '&:hover': {
      backgroundColor: 'var(--color-bg-subtle)',
      color: 'var(--color-primary)'
    }
  },

  // Spinner accent
  spinnerAccent: {
    color: 'var(--color-primary)'
  },

  // Canvas content column
  canvasContentColumn: {
    display: 'flex',
    flexDirection: 'column' as const
    // gap: 4
  },

  // LocalSeoPDFContainer states
  localSeoLoadingBox: {
    marginTop: 4,
    padding: 6,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-border-medium)',
    borderRadius: '12px',
    backgroundColor: '#FAFBFC'
  },
  localSeoSpinner: {
    color: 'var(--color-primary)',
    mb: 2
  },
  localSeoLoadingText: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-16)',
    color: 'var(--color-primary)'
  },
  localSeoErrorBox: {
    marginTop: 4,
    padding: 6,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #FDA29B',
    borderRadius: '12px',
    backgroundColor: '#FFF1F0'
  },
  localSeoErrorText: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-16)',
    color: 'var(--color-error-darkest)'
  },
  localSeoKeywordSectionBox: {
    // marginTop: '25px',
    breakInside: 'avoid' as const,
    pageBreakInside: 'avoid' as const
  },
  keywordSectionWrapper: {
    breakInside: 'avoid' as const,
    pageBreakInside: 'avoid' as const
  },
  competitorTableCard: {
    marginTop: '25px',
    border: '1px solid grey !important',
    borderRadius: '12px !important',
    boxShadow: 'none !important'
  },
  competitorTableCardContent: {
    padding: '24px !important'
  },
  competitorTableTitle: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: 'var(--font-size-24)',
    lineHeight: 'var(--line-height-32)',
    color: 'var(--color-primary)',
    my: '24px'
  },
  competitorTable: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  competitorTableHeaderRow: {
    backgroundColor: 'var(--color-bg-subtle) !important'
  },
  competitorTableCell: {
    padding: '12px !important'
  },
  competitorTableCellIndex: {
    padding: '12px !important',
    width: '5%'
  },
  competitorTableCellNameWidth: {
    padding: '12px !important',
    width: '40%'
  },
  competitorTableCellRatingWidth: {
    padding: '12px !important',
    width: '15%'
  },
  competitorTableCellVisibilityWidth: {
    padding: '12px !important',
    width: '20%',
    textAlign: 'center' as const
  },
  competitorTableCellArpWidth: {
    padding: '12px !important',
    width: '15%',
    textAlign: 'center' as const
  },
  competitorCellIndex: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    color: 'var(--color-text-tertiary)'
  },
  competitorCellName: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-16)',
    fontWeight: 500,
    color: 'var(--color-text-darkest)',
    lineHeight: 'var(--line-height-22)'
  },
  competitorCellValue: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-16)',
    color: 'var(--color-text-darkest)',
    lineHeight: 'var(--line-height-22)',
    fontWeight: 500
  },
  competitorBodyRow: {
    borderBottom: '1px solid var(--color-border-medium)',
    '&:last-child': { borderBottom: 'none' }
  },
  competitorTableContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 1
  },
  competitorTitleKeyword: {
    fontWeight: 700
  },
  competitorTitleSuffix: {
    fontWeight: 500
  },

  // AISummaryPreview inline styles
  aiPreviewLoadingText: {
    mt: 2,
    color: 'var(--color-text-dark)'
  },
  aiPreviewHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2
  },
  aiPreviewIconCircle: {
    backgroundColor: 'var(--color-white)',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
  },
  aiPreviewEmojiText: {
    fontSize: 'var(--font-size-16)'
  },
  aiPreviewTitle: {
    fontSize: 'var(--font-size-16)',
    fontWeight: 600,
    color: 'var(--color-text-darkest)'
  },
  aiPreviewRefreshBox: {
    marginLeft: 'auto'
  },
  aiPreviewRefreshBtn: {
    color: 'var(--color-primary)'
  },
  aiPreviewRefreshBoxWithMargin: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  aiPreviewCollapseBtn: {
    color: '#101828',
    p: 0,
    ml: 'auto',
    '&:hover': { backgroundColor: 'transparent', opacity: 0.7 }
  },
  aiPreviewEmptyBox: {
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    opacity: 0.7
  },
  aiPreviewEmptyText: {
    color: '#123C34',
    fontWeight: 500
  },
  aiPreviewSectionWrapper: {
    marginBottom: '16px'
  },
  aiPreviewSectionWrapperLarge: {
    marginBottom: '32px'
  },

  // AISummaryPlaceholder inline styles
  aiPlaceholderActionRow: {
    display: 'flex',
    gap: 2,
    mt: 2
  },
  aiPlaceholderRefreshBtn: {
    borderColor: '#120242',
    color: '#120242',
    '&:hover': {
      borderColor: '#0a012a',
      backgroundColor: 'rgba(18, 2, 66, 0.04)'
    }
  },

  // AISummaryConfig expand icon
  aiConfigExpandIcon: (expanded: boolean) => ({
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s'
  }),
  businessDropdownBoxWrapper: {
    marginTop: '-16px',
    marginBottom: '8px',
    '& > div:first-of-type': {
      marginLeft: '0px',
      width: '100%'
    }
  },
  keywordMultiSelect: {
    filterBoxSx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: 'white',
      minWidth: '100%',
      minHeight: '44px',
      borderRadius: '6px',
      padding: '8px 16px 8px 16px',
      border: '1px solid #D0D5DD',
      gap: '16px'
    },
    selectedFilterSx: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#120242'
    },
    optionBox: {
      px: 2,
      py: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F5F5F5'
      }
    },
    selectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#120242'
    },
    nonSelectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#1D2939'
    }
  },
  keywordMultiSelectDynamic: (hasError: boolean) => ({
    filterBoxSx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: 'white',
      minWidth: '100%',
      minHeight: '44px',
      borderRadius: '6px',
      padding: '8px 16px',
      border: `1px solid ${hasError ? '#F04438' : '#D0D5DD'}`,
      gap: '16px'
    },
    selectedFilterSx: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#120242'
    },
    optionBox: {
      px: 2,
      py: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F5F5F5'
      }
    },
    selectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#120242'
    },
    nonSelectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#1D2939'
    }
  }),

  // PreviewPlaceholder styles
  previewPlaceholderRoot: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    width: '100%'
  },
  previewPlaceholderContent: (theme: Theme) => ({
    flexGrow: 1,
    p: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto' as const,
    backgroundColor:
      theme.palette.mode === 'dark' ? 'background.default' : 'grey.100'
  }),
  previewPlaceholderPaper: {
    width: '100%',
    maxWidth: 600,
    aspectRatio: '1 / 1.414',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed',
    borderColor: 'text.secondary',
    bgcolor: 'background.paper',
    maxHeight: '100%'
  },

  // PreviewHeader inline styles
  previewHeaderSpacer: {
    width: 140
  },
  previewHeaderActions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },

  // PDFViewport inline styles
  viewportErrorPaper: {
    p: 4,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    maxWidth: 400
  },
  viewportErrorIcon: {
    fontSize: 48,
    mb: 2
  },
  ai_summary_loader: {
    textAlign: 'center',
    padding: '24px 0'
  },
  loadingTextMain: {
    mt: 0,
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    color: '#667085'
  }
};

export const PDFBuilderCSS = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    bgcolor: 'white',
    position: 'relative'
  },
  mainContent: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative' as const
  },
  sidebarWrapper: {
    width: 300,
    flexShrink: 0,
    height: '100%',
    overflowY: 'auto' as const,
    borderLeft: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    display: { xs: 'none', md: 'block' }
  },
  sidebarDrawer: {
    '& .MuiDrawer-paper': {
      width: 300,
      boxSizing: 'border-box'
    }
  },
  previewArea: (isAnyPreviewLoading: boolean) => ({
    flexGrow: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    bgcolor: 'var(--color-bg-subtle)',
    position: 'relative' as const,
    overflowY: isAnyPreviewLoading ? 'hidden' : ('auto' as const),
    overflowX: 'auto' as const,
    alignItems: { xs: 'flex-start', lg: 'center' } as any,
    p: { xs: 1, sm: 2, md: 4 }
  }),
  previewVisibility: (isAnyPreviewLoading: boolean) => ({
    display: isAnyPreviewLoading ? 'none' : 'block'
  }),
  canvasCard: {
    boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
    borderRadius: '8px',
    bgcolor: 'white',
    width: 'fit-content',
    my: 2
  },
  hiddenRenderContainer: {
    position: 'absolute' as const,
    left: '-10000px',
    top: 0,
    width: '1130px' // Ensure enough horizontal space for the canvas during capture
  },
  hiddenPdfWrapper: {
    p: 0,
    bgcolor: 'white',
    width: 'fit-content'
  }
};

export const NavBarCSS = {
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    px: { xs: 1.5, sm: 2, md: 3 },
    py: 1,
    gap: 0.5,
    backgroundColor: 'var(--color-white)',
    borderBottom: '1px solid var(--color-border-medium)',
    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
    zIndex: 1100
  },
  // Single-row on large screens (logo + dates + actions), stacked on small
  singleRow: {
    display: { xs: 'none', lg: 'flex' },
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 1
  },
  // Two-row layout for small/medium screens
  topRow: {
    display: { xs: 'flex', lg: 'none' },
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  dateRowSmall: {
    display: { xs: 'flex', lg: 'none' },
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: { xs: '4px', md: '6px' },
    width: '100%',
    overflowX: 'auto' as const,
    pb: 0.5
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  logoImage: {
    height: '32px',
    cursor: 'pointer'
  },
  hamburgerBtn: {
    display: { xs: 'flex', md: 'none' },
    color: 'var(--color-text-dark)',
    p: 0.5
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 1, md: 1.5 }
  },
  bulkEditBtn: {
    backgroundColor: 'var(--color-bg-subtle)',
    color: 'var(--color-text-dark)',
    textTransform: 'none',
    borderRadius: '8px',
    border: '1px solid var(--color-border)',
    px: 2,
    '&:hover': {
      backgroundColor: 'var(--color-bg-muted)'
    },
    maxHeight: '40px',
    fontWeight: 600
  },
  aiSummaryBtn: {
    background:
      'linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 170%)',
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    '&:hover': {
      background:
        'linear-gradient(180deg, var(--color-purple-deep) 0%, var(--color-secondary-darker) 170%)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  generateBtn: {
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    '&:hover': {
      backgroundColor: 'var(--color-purple-deep)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: '176px'
  },
  settingsBtn: {
    color: 'var(--color-text-tertiary)',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    p: 1,
    '&:hover': {
      backgroundColor: 'var(--color-bg-subtle)'
    }
  },
  // Date selection (centered in single-row on lg+)
  dateSelectionContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap' as const,
    gap: '6px',
    flex: '1 1 auto',
    justifyContent: 'center',
    minWidth: 0
  },
  periodBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: '4px', md: '8px' }
  },
  periodLabel: {
    fontFamily: 'Inter',
    fontSize: { xs: '12px', md: '14px' },
    fontWeight: 500,
    color: 'var(--color-text-dark)',
    whiteSpace: 'nowrap'
  },
  dateInputBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    padding: { xs: '0 8px', md: '0 12px' },
    backgroundColor: 'var(--color-white)',
    cursor: 'pointer',
    width: 'auto',
    minWidth: 0,
    height: { xs: '34px', md: '38px' },
    '&:hover': {
      borderColor: 'var(--color-text-placeholder)'
    }
  },
  dateText: {
    fontFamily: 'Inter',
    fontSize: { xs: '12px', md: '14px' },
    color: 'var(--color-text-darkest)',
    fontWeight: 500,
    whiteSpace: 'nowrap'
  },
  calendarIcon: {
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-20)'
  },
  compareLabel: {
    fontFamily: 'Inter',
    fontSize: { xs: '12px', md: '14px' },
    fontWeight: 500,
    color: 'var(--color-text-dark)',
    whiteSpace: 'nowrap'
  },
  warningIcon: {
    color: 'var(--color-warning-orange)',
    fontSize: 'var(--font-size-20)'
  },
  hiddenTextField: {
    display: 'none'
  },
  // NavBar compare box margin
  periodBoxWithMargin: {
    marginLeft: { xs: '4px', md: '12px' }
  },
  // Combined: periodBox + periodBoxWithMargin
  compareBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: '4px', md: '8px' },
    marginLeft: { xs: '4px', md: '12px' }
  },
  // AI Summary button — desktop (visible sm+)
  aiSummaryBtnDesktop: {
    //background: 'linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 170%)',
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    '&:hover': {
      background:
        'linear-gradient(180deg, var(--color-purple-deep) 0%, var(--color-secondary-darker) 170%)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: { xs: 'none', sm: 'inline-flex' }
  },
  // AI Summary icon button — mobile (visible xs only)
  aiSummaryBtnMobile: {
    background:
      'linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 170%)',
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    '&:hover': {
      background:
        'linear-gradient(180deg, var(--color-purple-deep) 0%, var(--color-secondary-darker) 170%)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: { xs: 'inline-flex', sm: 'none' },
    minWidth: 'auto',
    width: 34,
    height: 34
  },
  // AI icon inside mobile button
  aiSummaryIcon: {
    fontSize: 18
  },
  // Generate button — desktop (visible sm+)
  generateBtnDesktop: {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    '&:hover': {
      backgroundColor: 'var(--color-purple-deep)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: '176px',
    display: { xs: 'none', sm: 'inline-flex' }
  },
  // Generate button — mobile (visible xs only)
  generateBtnMobile: {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    textTransform: 'none',
    borderRadius: '8px',
    px: 1.5,
    fontSize: '12px',
    '&:hover': {
      backgroundColor: 'var(--color-purple-deep)'
    },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: '176px',
    display: { xs: 'inline-flex', sm: 'none' }
  },
  // Dynamic Button Styles
  aiSummaryBtnDynamic: (hasAnySections: boolean) => ({
    color: 'white',
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    background: !hasAnySections
      ? '#dcdcdc'
      : 'linear-gradient(180deg, #120242 0%, #25D3B1 170%)',
    display: { xs: 'none', sm: 'inline-flex' },
    '&:hover': {
      background: 'linear-gradient(180deg, #0d0131 0%, #1fb496 170%)'
    }
  }),
  generateBtnDynamic: (disabled: boolean) => ({
    color: 'white',
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: { xs: 1.5, md: 2 },
    fontSize: { xs: '12px', md: '14px' },
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: '176px',
    backgroundColor: disabled ? '#dcdcdc' : '#120242',
    display: { xs: 'none', sm: 'inline-flex' },
    '&:hover': {
      backgroundColor: '#0d0131'
    }
  }),
  generateBtnMobileDynamic: (disabled: boolean) => ({
    backgroundColor: disabled ? '#dcdcdc' : '#120242',
    color: 'white',
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: 1.5,
    fontSize: '12px',
    maxHeight: { xs: '34px', md: '40px' },
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    minWidth: '176px',
    display: { xs: 'inline-flex', sm: 'none' },
    '&:hover': {
      backgroundColor: '#0d0131'
    }
  }),
  // White spinner for loading state
  spinnerWhite: {
    color: 'white'
  },

  // Custom Date Range Picker (within NavBar context)
  calendarContainer: {
    display: 'flex',
    flexDirection: 'column' as const
  },
  pickerPaper: {
    mt: 0.5,
    borderRadius: '10px',
    boxShadow: '0px 8px 12px -3px rgba(16, 24, 40, 0.08)',
    border: '1px solid #EAECF0',
    width: '800px'
  },
  calendarMonthWrapper: {
    p: '8px 12px',
    minWidth: '280px'
  },
  calendarMonthHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 1.5,
    minHeight: '32px'
  },
  monthTitle: {
    fontWeight: 700,
    color: '#101828',
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    letterSpacing: '0.01em',
    userSelect: 'none' as const
  },
  dayOfWeekHeader: {
    fontSize: '12px',
    color: '#667085',
    fontWeight: 500,
    lineHeight: 'var(--line-height-18)',
    textAlign: 'center' as const
  },
  calendarFooterWarning: {
    p: 1.5,
    backgroundColor: '#FFFAEB',
    borderTop: '1px solid #FEF0C7',
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  warningText: {
    fontSize: '12px',
    color: '#B54708',
    fontWeight: 500
  },
  presetItem: {
    py: 1,
    px: 2.5,
    '&.Mui-selected': {
      backgroundColor: '#F9FAFB',
      '&:hover': { backgroundColor: '#F2F4F7' }
    },
    '&:hover': { backgroundColor: '#F9FAFB' }
  },
  presetLabel: (selected: boolean) => ({
    fontSize: 'var(--font-size-14)',
    fontWeight: selected ? 500 : 400,
    color: selected ? '#120242' : '#344054',
    lineHeight: 'var(--line-height-20)'
  }),
  calendarGridContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  },
  calendarCell: (params: {
    isInRange: boolean;
    isStart: boolean;
    isEnd: boolean;
    isFirstCol: boolean;
    isLastCol: boolean;
  }) => {
    let cellBg = 'transparent';
    let cellBorderRadius = '0px';
    if (params.isInRange) {
      if (params.isStart && params.isEnd) {
        cellBg = 'transparent';
      } else if (params.isStart) {
        cellBg = 'linear-gradient(to right, transparent 50%, #EEF0F8 50%)';
      } else if (params.isEnd) {
        cellBg = 'linear-gradient(to left, transparent 50%, #EEF0F8 50%)';
      } else {
        cellBg = '#EEF0F8';
        if (params.isFirstCol) cellBorderRadius = '18px 0 0 18px';
        else if (params.isLastCol) cellBorderRadius = '0 18px 18px 0';
      }
    }
    return {
      textAlign: 'center' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'default',
      height: '36px',
      background: cellBg,
      borderRadius: cellBorderRadius
    };
  },
  calendarDayCircle: (isEndpoint: boolean) => ({
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isEndpoint ? '#120242' : 'transparent',
    borderRadius: isEndpoint ? '50%' : '0',
    flexShrink: 0
  }),
  calendarDayText: (params: { isEndpoint: boolean; textColor: string }) => ({
    fontSize: 'var(--font-size-14)',
    fontWeight: params.isEndpoint ? 600 : 400,
    color: params.textColor,
    lineHeight: 1
  }),
  aiConfigActionRow: {
    p: 2,
    mt: 'auto',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: 1
  }
};

export const RightSidebarCSS = {
  container: {
    width: 380,
    height: '100%',
    backgroundColor: 'white',
    borderLeft: '1px solid var(--color-border-medium)',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1000,
    boxShadow: '-4px 0px 10px rgba(0,0,0,0.05)'
  },
  header: {
    p: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid var(--color-border-medium)'
  },
  content: {
    p: 3,
    flexGrow: 1,
    overflowY: 'auto' as const
  },
  sidebarTitle: {
    fontWeight: 600
  },
  helperText: {
    mt: 2,
    display: 'block' as const,
    color: '#667085'
  }
};

export const DialogCSS = {
  paper: {
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '750px',
    width: '100%',
    overflow: 'hidden',
    paddingRight: '5px',
    fontFamily: 'Inter, sans-serif'
  },
  expandIcon: {
    color: '#120242'
  },
  dialogWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 48px)'
  },
  fixedHeader: {
    flexShrink: 0,
    paddingRight: '10px'
  },
  scrollableContent: {
    paddingRight: '10px',
    overflowY: 'auto' as const,
    flex: 1,
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'white',
      borderRadius: '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--color-border-medium)',
      borderRadius: '10px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'var(--color-border-medium)',
      borderRadius: '10px'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2
  },
  closeButton: {
    position: 'absolute' as const,
    right: 16,
    top: 16
  },
  headerFixed: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
    flexShrink: 0,
    paddingRight: '10px'
  },
  closeBtnRelative: {
    position: 'relative' as const,
    right: 0,
    top: 0
  },
  mainStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
    // mt: 2
  },
  switchLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    // mb: 2,
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: 'var(--font-size-18)',
      color: 'var(--color-text-darkest)'
    }
  },
  emailFieldBox: {
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    p: 1.5,
    mt: 1,
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 1,
    alignItems: 'center',
    bgcolor: 'white'
  },
  chip: {
    borderRadius: '6px',
    bgcolor: 'var(--color-bg-muted)',
    fontFamily: 'Inter',
    fontSize: '13px'
  },
  emailInput: {
    flexGrow: 1,
    ml: 1,
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
  },
  textField: {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    }
  },
  richTextEditor: {
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    mt: 2,

    '& .ql-snow.ql-toolbar:after, & .ql-snow .ql-toolbar:after': {
      content: 'none',
      display: 'none'
    },

    '& .ql-toolbar.ql-snow': {
      backgroundColor: 'var(--color-bg-purple-light2)',
      border: 'none',
      borderRadius: '8px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px'
    },

    '& .ql-container.ql-snow': {
      border: 'none',
      fontFamily: 'Inter, sans-serif',

      '& .ql-editor': {
        padding: '12px !important',
        fontSize: '20px !important',
        color: 'var(--color-text-darkest)',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxHeight: '124px',
        overflowY: 'auto',
        '& p': {
          fontSize: '20px !important'
        }
      }
    },

    '& .ql-editor.ql-blank': {
      padding: '0px !important',
      fontFamily: 'Inter, sans-serif',
      fontSize: '20px !important'
    }
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    pb: 1,

    '.ql-snow.ql-toolbar:after': {
      display: 'none !important',
      clear: 'both'
    }
  },
  toolbarActions: {
    display: 'flex',
    gap: 0.5,
    alignItems: 'center',
    '& button, & select': {
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: '6px',
      fontSize: '12px',
      color: 'var(--color-text-dark)'
    },
    '& button:hover, & select:hover': {
      backgroundColor: 'var(--color-bg-muted)'
    }
  },
  toolbarButton: {
    textTransform: 'none' as const,
    color: 'var(--color-text-dark)',
    borderColor: 'var(--color-border)',
    borderRadius: '8px'
  },
  previewTextGreenBody: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    lineHeight: '23px',
    fontWeight: 400,
    color: '#344054',
    whiteSpace: 'pre-line'
  },
  previewTextGreenHeader: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-18)',
    lineHeight: 'var(--line-height-28)',
    mb: 1.2,
    color: '#101828',
    textDecoration: 'underline',
    textUnderlineOffset: '4px'
  },
  previewTextGreenHeaderLarge: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-18)',
    lineHeight: 'var(--line-height-28)',
    mb: 1.5,
    color: '#101828',
    textDecoration: 'underline',
    textUnderlineOffset: '6px'
  },
  recentMessagesWrapper: {
    // display: 'flex',
    // alignItems: 'stretch',
    // width: 'max-content'
  },
  recentMessagesButton: {
    width: 'max-content !important',
    padding: '0px !important'
  },
  editorContent: {
    color: 'var(--color-text-tertiary)',
    minHeight: '80px',
    '& .ql-container': {
      border: 'none',
      fontFamily:
        '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '20px !important',
      color: 'var(--color-text-darkest)'
    },
    '& .ql-toolbar': {
      border: 'none',
      borderBottom: '1px solid var(--color-border-medium)',
      paddingLeft: 0,
      paddingRight: 0
    },
    '& .ql-editor': {
      minHeight: '120px',
      paddingLeft: 0,
      paddingRight: 0,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      maxHeight: '124px',
      overflowY: 'auto',
      fontSize: '20px !important',
      '& p': {
        fontSize: '20px !important'
      }
    }
  },
  signatureBox: {
    color: 'var(--color-text-tertiary)',
    mb: 2
  },
  imageUploadBtn: {
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    p: 1
  },
  customizationTitle: {
    fontWeight: 600,
    mb: 1.5,
    color: 'var(--color-text-dark)'
  },
  customizationStack: {
    mb: 2
  },
  secondaryStack: {
    mb: 3
  },
  frequencyBox: {
    display: 'flex',
    gap: 1,
    marginTop: '8px'
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end' as const,
    marginTop: '20px',
    gap: '16px'
  },
  generateBtn: {
    borderRadius: '8px',
    px: 4,
    py: 1.5,
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    maxHeight: '44px',
    minWidth: '179px'
  },
  saveBtn: {
    borderRadius: '8px',
    px: 4,
    py: 1.5,
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    maxHeight: '44px',
    backgroundColor: 'transparent',
    color: 'var(--color-primary)',
    borderColor: 'rgba(18, 2, 66, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(18, 2, 66, 0.04)',
      borderColor: 'var(--color-primary)'
    }
  },
  toggleGroup: {
    backgroundColor: '#F2F4F7',
    borderRadius: '8px',
    '& .MuiToggleButtonGroup-grouped': {
      border: 'none',
      margin: '0 2px',
      '&.Mui-selected': {
        backgroundColor: 'white',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
        color: '#120242',
        '&:hover': {
          backgroundColor: 'white'
        }
      }
    }
  },
  toggleButton: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    color: '#667085',
    borderRadius: '6px !important',
    py: 1
  },
  previewButton: {
    borderRadius: '8px',
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    color: '#120242',
    borderColor: '#D0D5DD'
  },
  actions: {
    padding: '16px 24px',
    gap: 1.5
  },
  cancelButton: {
    borderRadius: '8px',
    px: 3,
    py: 1,
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '14px',
    color: '#344054',
    borderColor: '#D0D5DD'
  },
  applyButton: {
    borderRadius: '8px',
    px: 3,
    py: 1,
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    backgroundColor: '#120242',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0d0131'
    }
  },
  applyButtonDynamic: (disabled: boolean) => ({
    borderRadius: '8px',
    px: 3,
    py: 1,
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    backgroundColor: disabled ? '#F2F4F7' : '#120242',
    color: disabled ? '#98A2B3' : 'white',
    '&:hover': {
      backgroundColor: disabled ? '#F2F4F7' : '#0d0131'
    },
    '&.Mui-disabled': {
      backgroundColor: '#F2F4F7',
      color: '#98A2B3'
    }
  }),
  alertSpacing: {
    mb: 1,
    mt: 1
  },
  editorFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 1,
    px: 1
  },
  editorErrorMessage: {
    color: '#d32f2f',
    fontSize: '12px',
    fontWeight: 500
  },
  charCountText: (isOverLimit: boolean) => ({
    fontSize: '12px',
    color: isOverLimit ? '#d32f2f' : '#667085',
    fontWeight: isOverLimit ? 600 : 400
  }),
  popoverHeaderWrapper: {
    px: { xs: 2, sm: 2.5 },
    pt: { xs: 1.5, sm: 2 },
    flexShrink: 0
  },
  popoverScrollContent: {
    px: { xs: 2, sm: 2.5 },
    py: 2,
    flexGrow: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: '#EAECF0',
      borderRadius: '10px'
    }
  },
  popoverFooterWrapper: {
    px: { xs: 2, sm: 2.5 },
    pb: { xs: 2, sm: 2.5 },
    pt: 0,
    flexShrink: 0
  },
  dayPickerPaper: {
    mb: 1,
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    p: 1.5
  },
  dayPickerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px'
  },
  dayPickerCell: (selected: boolean) => ({
    width: 34,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    cursor: 'pointer',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: selected ? 600 : 400,
    color: selected ? '#fff' : '#1D2939',
    backgroundColor: selected ? '#120242' : 'transparent',
    '&:hover': {
      backgroundColor: selected ? '#120242' : '#F2F4F7'
    }
  }),
  popoverStyles: {
    filterBoxSx: {
      width: 'fit-content',
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      px: 2,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      cursor: 'pointer'
    },
    selectedFilterSx: { color: 'var(--color-primary)', fontWeight: 400 }
  },
  popoverStylesDynamic: {
    filterBoxSx: {
      width: 'fit-content',
      border: '1px solid #D0D5DD',
      borderRadius: '8px',
      px: 2,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      cursor: 'pointer'
    },
    selectedFilterSx: {
      color: '#120242',
      fontWeight: 400
    },
    optionBox: {
      px: 2,
      py: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F5F5F5'
      }
    },
    selectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#120242'
    },
    nonSelectedOptionText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 'var(--font-size-14)',
      lineHeight: 'var(--line-height-20)',
      color: '#1D2939'
    }
  },
  // AI Summary Modal Styles
  fieldLabel: {
    color: 'var(--color-text-purple)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    mb: '8px'
  },
  sectionTitle: {
    color: 'var(--color-text-mid)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-20)',
    lineHeight: 'var(--line-height-24)',
    mb: '8px'
  },
  // AI Summary Popover paper
  aiPopoverPaper: {
    borderRadius: '12px',
    padding: '0px',
    maxWidth: { xs: 'calc(100vw - 32px)', sm: '500px' },
    width: { xs: 'calc(100vw - 32px)', sm: '500px' },
    mt: 1,
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
    maxHeight: { xs: 'calc(100vh - 100px)', sm: 'none' },
    overflowY: 'auto' as const
  },
  aiPopoverPaperDynamic: {
    borderRadius: '12px',
    padding: '0px',
    maxWidth: { xs: 'calc(100vw - 32px)', sm: '500px' },
    width: { xs: 'calc(100vw - 32px)', sm: '500px' },
    mt: 1,
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: { xs: 'calc(100vh - 48px)', sm: '80vh' },
    overflow: 'hidden',
    p: 0
  },
  dividerSmall: {
    my: 1
  },
  dividerMedium: {
    my: 2
  },
  // Dialog title inline styles
  dialogTitleText: {
    p: 0,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-20)',
    color: 'var(--color-primary)'
  },
  dialogCloseRelative: {
    position: 'relative' as const,
    right: 0,
    top: 0
  },
  // FormControlLabel column-reverse
  formLabelColumnReverse: {
    display: 'flex',
    flexDirection: 'column-reverse' as const,
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Inter',
      fontSize: 'var(--font-size-14)',
      fontWeight: 500,
      color: 'var(--color-text-purple)',
      lineHeight: 'var(--line-height-20)',
      mb: '6px'
    }
  },
  switchLabelInline: {
    marginLeft: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Inter',
      fontSize: 'var(--font-size-14)',
      fontWeight: 500,
      color: 'var(--color-text-purple)'
    }
  },
  switchLabelEmail: {
    marginLeft: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Inter',
      fontSize: 'var(--font-size-14)',
      fontWeight: 500,
      color: 'var(--color-text-purple)'
    }
  },
  emailContentBox: {
    mt: 1
  },
  previewBoxGreen: {
    background: 'linear-gradient(135deg, #D4F4DD 0%, #E8F9ED 100%)',
    border: '1px solid #A6E3B8',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '16px'
  },
  previewLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 0',
    gap: 1
  },
  errorCaption: {
    fontFamily: 'Inter',
    mt: 0.5,
    display: 'block',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    textAlign: 'left' as const,
    marginTop: '3px',
    marginRight: '14px',
    marginBottom: '0',
    marginLeft: '14px',
    color: 'error.main'
  },
  helperCaption: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: 'var(--color-text-purple)',
    mt: 0.5,
    display: 'block',
    fontWeight: 500
  },
  scheduleMainRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap' as const,
    mt: 2
  },
  scheduleSubRow: {
    display: 'flex',
    gap: 1,
    alignItems: 'center'
  },
  sidebarTitleWithMargin: {
    mb: 3
  },
  dividerBottom: {
    mb: 3
  },
  businessDropdownBox: {
    marginTop: '-16px',
    marginBottom: '8px'
  }
};

export const LocalSeoPDFCSS = {
  container: (isExporting: boolean, hideMapRanking: boolean) => ({
    marginTop: 4,
    '& button': {
      display: 'none !important'
    },
    '& .MuiSvgIcon-root[data-testid="HelpIcon"]': {
      display: 'none !important'
    },
    '& svg[data-testid="HelpIcon"]': {
      display: 'none !important'
    },
    '& .YourPerformanceCardSection': {
      border: '1px solid rgb(208, 213, 221) !important',
      borderRadius: '12px !important',
      backgroundColor: '#fff !important',
      marginTop: '25px !important',
      boxSizing: 'border-box !important'
    },
    '& .YourPerformanceCardSection .MuiCard-root': {
      border: 'none !important',
      boxShadow: 'none !important',
      marginTop: '0 !important',
      backgroundColor: 'transparent !important'
    },
    '& .YourPerformanceCardSection .MuiCardContent-root': {
      padding: '0 !important'
    },
    '& .YourPerformanceCardSection .keywordMap': {
      paddingX: 3,
      paddingBottom: 3,
      marginTop: '24px !important'
    },
    '& .LocalSeoPDFDashBoardWrapper': {
      breakInside: 'avoid !important',
      pageBreakInside: 'avoid !important',
      display: 'block !important'
    },
    '& .localSeoKeywordSectionBox': {
      breakInside: 'avoid !important',
      pageBreakInside: 'avoid !important',
      display: 'block !important',
      padding: '24px !important'
    },
    '& .LocalSeoPDFDashBoardWrapper .LocalSeoMapDashBoardContainer': {
      display: 'none !important'
    },
    '& .YourPerformanceCardSection .MuiCardContent-root > .MuiBox-root:first-of-type > .MuiBox-root:last-of-type':
      {
        display: 'none !important'
      },
    '& .LocalSeoPerformanceMetricsRow': {
      display: 'flex !important',
      flexDirection: 'row !important',
      flexWrap: 'nowrap !important',
      alignItems: 'stretch !important',
      marginTop: '24px !important',
      gap: '16px !important',
      width: '100% !important',
      minWidth: '100% !important',
      breakInside: 'avoid !important',
      pageBreakInside: 'avoid !important',
      overflow: 'visible !important'
    },
    '& .LocalSeoPerformanceMetricsRow > .MuiBox-root, & .LocalSeoPerformanceMetricsRow .LocalSeoSubCard':
      {
        flex: '1 1 0% !important',
        width: '0 !important',
        maxWidth: '25% !important',
        minWidth: '0 !important',
        boxSizing: 'border-box !important',
        borderRadius: '12px !important',
        border: '1px solid rgb(208, 213, 221) !important',
        backgroundColor: 'transparent !important',
        padding: '16px !important',
        display: 'flex !important',
        flexDirection: 'column !important',
        justifyContent: 'space-between !important',
        breakInside: 'avoid !important',
        pageBreakInside: 'avoid !important',
        overflow: 'hidden !important'
      },
    '& .LocalSeoPerformanceMetricsRow .recharts-responsive-container': {
      width: '100% !important',
      minHeight: '80px !important'
    },
    '& .LocalSeoPerformanceMetricsRow .MuiLinearProgress-root': {
      display: 'block !important',
      visibility: 'visible !important',
      width: '100% !important'
    },
    '& .LocalSeoKeywordSelector': {
      marginTop: '0px !important',
      marginBottom: '14px !important'
    },
    '& .LocalSeoKeywordSelector > .MuiTypography-root:first-of-type': {
      display: 'none !important'
    },
    '& .LocalSeoKeywordSelector > .MuiBox-root': {
      border: 'none !important',
      padding: '0 !important',
      backgroundColor: 'transparent !important',
      minHeight: 'auto !important',
      display: 'flex',
      alignItems: 'center',
      cursor: 'default !important',
      pointerEvents: 'none !important'
    },
    '& .LocalSeoKeywordSelector .MuiTypography-root': {
      fontSize: '22px !important',
      fontWeight: '680 !important',
      color: '#120242 !important'
    },
    '& .LocalSeoKeywordSelector .MuiTypography-root::before': {
      content: '"Keyword: "',
      fontWeight: 680,
      marginRight: '6px',
      color: '#120242',
      fontSize: '22px !important'
    },
    '& .LocalSeoKeywordSelector svg': {
      display: 'none !important'
    },
    '& .LocalSeoMetricsGrid': {
      display: 'flex !important',
      flexDirection: 'row !important',
      flexWrap: 'nowrap !important',
      marginTop: '12px !important',
      gap: '16px !important'
    },
    '& .LocalSeoMetricCard': {
      flex: '1 1 0% !important',
      minWidth: '0 !important',
      width: '0 !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important',
      borderRadius: '12px !important',
      border: '1px solid grey !important',
      padding: '16px !important',
      backgroundColor: 'transparent !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      justifyContent: 'space-between !important'
    },
    '& .LocalSeoMetricTitle': {
      display: 'block !important',
      visibility: 'visible !important',
      opacity: '1 !important',
      color: '#120242 !important',
      fontWeight: '680 !important',
      fontSize: '20px !important',
      lineHeight: '22px !important',
      marginBottom: '8px !important'
    },
    '& .LocalSeoMetricValue': {
      display: 'block !important',
      visibility: 'visible !important',
      opacity: '1 !important',
      color: '#120242 !important',
      fontWeight: '700 !important',
      fontSize: '24px !important'
    },
    '& .LocalSeoMetricRowTitle, & .LocalSeoMetricRowValue': {
      color: '#120242 !important',
      fontWeight: '600 !important'
    },
    '& .LocalSeoMetricCard .MuiTypography-root': {
      display: 'block !important',
      visibility: 'visible !important',
      opacity: '1 !important'
    },
    '& .LocalSeoMapDashBoardContainer': {
      width: '100% !important',
      marginTop: '25px !important',
      border: 'none !important',
      borderRadius: '0 !important',
      padding: '0 !important',
      backgroundColor: 'transparent !important',
      boxShadow: 'none !important',
      display: 'block !important',
      breakInside: 'avoid !important',
      pageBreakInside: 'avoid !important'
    },
    '& .LocalSeoMapDashBoardContainer > .MuiBox-root:first-of-type': {
      marginBottom: '24px !important'
    },
    '& .LocalSeoMapLiveContainer, & .LocalSeoMapPreview, & .LocalSeoMapFinal': {
      width: '100% !important',
      display: 'block !important',
      marginTop: '24px !important',
      minHeight: '600px !important',
      border: 'none !important'
    },
    '& .LocalSeoMapPreview': {
      display: isExporting ? 'none !important' : 'block !important'
    },
    '& .LocalSeoMapFinal': {
      display: isExporting ? 'block !important' : 'none !important'
    },
    ...(hideMapRanking && {
      '& .LocalSeoMapDashBoardContainer': {
        display: 'none !important'
      }
    }),
    '& .MapRankerProgress': {
      display: 'none !important'
    },
    '& .MuiCircularProgress-root': {
      display: 'block !important',
      visibility: 'visible !important',
      margin: '0 auto'
    }
  }),
  categoryText: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    color: '#667085',
    lineHeight: 'var(--line-height-20)',
    fontWeight: 500
  },
  visibilityDot: (color: string) => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: color
  }),
  dashPlaceholder: {
    width: 10,
    height: 1
  }
};

export const DatePickerCSS = {
  actions: {
    display: 'flex',
    padding: '16px 24px',
    borderTop: '1px solid #EAECF0',
    gap: '12px',
    justifyContent: 'flex-end' as const
  },
  toggleGroup: {
    height: '40px',
    '& .MuiToggleButtonGroup-grouped': {
      border: '1px solid var(--color-border)',
      '&:not(:first-of-type)': {
        borderLeft: '1px solid var(--color-border)',
        borderTopLeftRadius: '0px !important',
        borderBottomLeftRadius: '0px !important'
      },
      '&:not(:last-of-type)': {
        borderTopRightRadius: '0px !important',
        borderBottomRightRadius: '0px !important'
      },
      '&:first-of-type': {
        borderTopLeftRadius: '6px !important',
        borderBottomLeftRadius: '6px !important'
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px !important',
        borderBottomRightRadius: '6px !important'
      }
    }
  },
  toggleButton: {
    textTransform: 'none' as const,
    fontSize: 'var(--font-size-14)',
    fontWeight: 500,
    color: 'var(--color-text-dark)',
    py: 0,
    minHeight: '40px',
    '&.Mui-selected': {
      backgroundColor: 'var(--color-primary) !important',
      color: 'var(--color-white)',
      '&:hover': {
        backgroundColor: 'var(--color-purple-deep) !important'
      }
    }
  },
  select: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-border)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-text-placeholder)'
    }
  },
  previewBox: {
    backgroundColor: 'var(--color-bg-subtle)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: '8px',
    p: 3,
    mt: 0,
    minHeight: '150px'
  },
  previewBoxGreen: {
    background: 'linear-gradient(135deg, #D4F4DD 0%, #E8F9ED 100%)',
    border: '1px solid var(--color-success-pastel)',
    borderRadius: '8px',
    p: 3,
    mt: 0,
    minHeight: '100px'
  },
  previewText: {
    fontSize: 'var(--font-size-14)',
    lineHeight: 1.6,
    color: 'var(--color-text-mid)',
    whiteSpace: 'pre-wrap'
  },
  previewTextGreen: {
    fontSize: 'var(--font-size-14)',
    lineHeight: 1.6,
    color: 'var(--color-text-darkest)',
    whiteSpace: 'pre-wrap'
  },
  previewLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '150px'
  },
  previewButton: {
    textTransform: 'none' as const,
    fontWeight: 600,
    borderColor: 'var(--color-border)',
    color: 'var(--color-text-dark)',
    maxHeight: '40px',
    borderRadius: '6px',
    '&:hover': {
      borderColor: 'var(--color-text-placeholder)',
      backgroundColor: 'var(--color-bg-subtle)'
    }
  },
  //actions: {
  //  display: 'flex',
  //  gap: 2,
  //  flexDirection: 'row-reverse' as const
  //},
  cancelButton: {
    textTransform: 'none' as const,
    fontWeight: 600,
    color: 'var(--color-text-dark)',
    borderColor: 'var(--color-border)',
    maxHeight: '40px',
    borderRadius: '6px',
    '&:hover': {
      borderColor: 'var(--color-text-placeholder)',
      backgroundColor: 'var(--color-bg-subtle)'
    }
  },
  applyButton: {
    textTransform: 'none' as const,
    fontWeight: 600,
    backgroundColor: 'var(--color-primary)',
    maxHeight: '40px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: 'var(--color-purple-deep)'
    }
  },
  businessDropdownBox: {
    marginTop: '-16px',
    marginBottom: '8px'
  }
};

/* ─────────────────────────── Export Success Modal ─────────────────────────── */
const ES_PRIMARY = 'var(--color-primary)';
const ES_PRIMARY_HOVER = 'var(--color-purple-deep)';
const ES_BORDER = 'var(--color-border)';
const ES_TEXT_DARK = 'var(--color-text-darkest)';
const ES_TEXT_MED = 'var(--color-text-dark)';
const ES_TEXT_MUTED = 'var(--color-text-tertiary)';
const ES_BG_SUBTLE = 'var(--color-bg-subtle)';
const ES_ACCENT_BG = '#EEE9FF';
const ES_ACCENT_BG_HOVER = '#E3DCFF';
const ES_SURFACE = 'var(--color-near-white)';
const ES_RADIUS = '8px';
const ES_RADIUS_LG = '12px';

export const ExportSuccessCSS = {
  root: {
    position: 'relative' as const,
    px: '24px',
    pt: '24px',
    pb: '24px',
    fontFamily: 'Inter, sans-serif'
  },

  /* Close button */
  closeBtn: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    color: ES_TEXT_MUTED,
    p: '6px',
    borderRadius: ES_RADIUS,
    transition: 'all 0.15s ease',
    '&:hover': { backgroundColor: 'var(--color-bg-muted)', color: ES_TEXT_MED }
  },
  closeIconSmall: {
    fontSize: 'var(--font-size-18)'
  },

  /* ── Success Banner ── */
  successBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    mb: '24px',
    pr: '40px' // room for close btn
  },
  successIconRing: {
    width: 44,
    height: 44,
    borderRadius: ES_RADIUS_LG,
    background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0px 1px 3px rgba(16, 185, 129, 0.12)'
  },
  successIcon: {
    color: 'var(--color-success-medium2)',
    fontSize: 'var(--font-size-24)'
  },
  title: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-18)',
    color: ES_TEXT_DARK,
    lineHeight: 'var(--line-height-24)'
  },
  subtitle: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    fontWeight: 400,
    color: ES_TEXT_MUTED,
    mt: '4px',
    lineHeight: 'var(--line-height-20)'
  },
  siteName: {
    fontWeight: 500,
    color: ES_TEXT_MED
  },

  /* ── Download note ── */
  downloadNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    backgroundColor: ES_BG_SUBTLE,
    border: '1px solid var(--color-border-medium)',
    borderRadius: ES_RADIUS,
    px: '14px',
    py: '12px',
    mb: '24px'
  },
  downloadNoteIcon: {
    color: ES_TEXT_MUTED,
    fontSize: 'var(--font-size-18)',
    flexShrink: 0,
    mt: '1px'
  },
  downloadNoteText: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    fontWeight: 400,
    color: ES_TEXT_MED,
    lineHeight: 'var(--line-height-20)'
  },
  textLink: {
    color: ES_PRIMARY,
    fontWeight: 500,
    textDecoration: 'underline',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
    '&:hover': { color: ES_PRIMARY_HOVER }
  },

  /* ── Divider ── */
  divider: {
    height: '1px',
    backgroundColor: 'var(--color-border-medium)',
    mb: '20px'
  },

  /* ── Share label ── */
  shareLabel: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    fontWeight: 600,
    color: ES_TEXT_DARK,
    lineHeight: 'var(--line-height-20)',
    mb: '12px'
  },

  /* ── Tab Switcher ── */
  tabRow: {
    display: 'flex',
    gap: '8px',
    mb: '16px'
  },
  tabActive: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 'var(--font-size-14)',
    backgroundColor: ES_ACCENT_BG,
    color: ES_PRIMARY,
    borderRadius: ES_RADIUS,
    px: '16px',
    py: '6px',
    minHeight: 0,
    lineHeight: 'var(--line-height-20)',
    transition: 'all 0.15s ease',
    '&:hover': { backgroundColor: ES_ACCENT_BG_HOVER }
  },
  tabInactive: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    color: ES_TEXT_MUTED,
    borderRadius: ES_RADIUS,
    px: '16px',
    py: '6px',
    minHeight: 0,
    lineHeight: 'var(--line-height-20)',
    transition: 'all 0.15s ease',
    '&:hover': { backgroundColor: 'var(--color-bg-muted)', color: ES_TEXT_MED }
  },

  /* ── Panel container ── */
  panel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },

  /* ── Link Panel ── */
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: `1px solid ${ES_BORDER}`,
    borderRadius: ES_RADIUS,
    p: '6px 6px 6px 14px',
    backgroundColor: ES_SURFACE,
    transition: 'border-color 0.15s ease',
    '&:hover': { borderColor: 'var(--color-text-placeholder)' }
  },
  linkBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0
  },
  linkBoxIcon: {
    color: 'var(--color-text-placeholder)',
    fontSize: 'var(--font-size-18)',
    flexShrink: 0
  },
  linkText: {
    fontFamily: 'Inter',
    fontSize: 'var(--font-size-14)',
    fontWeight: 400,
    color: ES_TEXT_MED,
    lineHeight: 'var(--line-height-20)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  linkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0
  },

  /* ── Copy / Copied buttons ── */
  copyBtn: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    backgroundColor: ES_PRIMARY,
    color: 'var(--color-white)',
    borderRadius: ES_RADIUS,
    px: '16px',
    height: '36px',
    minWidth: 0,
    boxShadow: '0px 1px 2px rgba(18, 2, 66, 0.15)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: ES_PRIMARY_HOVER,
      boxShadow: '0px 2px 4px rgba(18, 2, 66, 0.2)'
    },
    whiteSpace: 'nowrap'
  },
  copiedBtn: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    backgroundColor: 'var(--color-success-medium2)',
    color: 'var(--color-white)',
    borderRadius: ES_RADIUS,
    px: '16px',
    height: '36px',
    minWidth: 0,
    boxShadow: '0px 1px 2px rgba(5, 150, 105, 0.2)',
    '&:hover': { backgroundColor: '#047857' },
    whiteSpace: 'nowrap'
  },
  openBtn: {
    color: ES_TEXT_MUTED,
    borderRadius: ES_RADIUS,
    p: '7px',
    flexShrink: 0,
    border: '1px solid var(--color-border-medium)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-bg-muted)',
      color: ES_PRIMARY,
      borderColor: ES_BORDER
    }
  },

  /* ── Embed Panel ── */
  embedBox: {
    border: `1px solid ${ES_BORDER}`,
    borderRadius: ES_RADIUS,
    backgroundColor: ES_BG_SUBTLE,
    p: '14px',
    overflow: 'auto',
    maxHeight: '140px',
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--color-border-medium)',
      borderRadius: '3px'
    }
  },
  embedCode: {
    fontFamily: '"Fira Code", "Courier New", monospace',
    fontSize: '12px',
    fontWeight: 400,
    color: ES_TEXT_MED,
    lineHeight: 1.7,
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const
  },
  copyOutlineBtn: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    borderColor: ES_BORDER,
    color: ES_TEXT_MED,
    borderRadius: ES_RADIUS,
    height: '40px',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: 'var(--color-text-placeholder)',
      backgroundColor: ES_BG_SUBTLE
    }
  },
  copiedOutlineBtn: {
    textTransform: 'none' as const,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 'var(--font-size-14)',
    borderColor: 'var(--color-success-medium2)',
    color: 'var(--color-success-medium2)',
    borderRadius: ES_RADIUS,
    height: '40px',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#047857',
      backgroundColor: 'var(--color-bg-mint)'
    }
  }
};
